import * as React from "react";
import {Button, Checkbox, Form, Input, Label} from 'semantic-ui-react';
import { authentication } from "../../../../../authentication";
import { ScriptingRequest, TransportOrder } from "../../../../../generated/api";
import { backend } from "../../../../../xconvert-backend";
import { PageWithSidebar } from "../../../../style/page_with_sidebar";
import { ConfigContext } from "../../../context/ConfigContext";
import AceEditor from 'react-ace';

import 'brace/mode/groovy'
import 'brace/ext/searchbox'
import { LogView } from "../../logView/LogView";

export interface ScriptExecutionState {
    isRunning: boolean
    validJson: boolean
    scriptExeToId: string
    xRequestId: string
    clientContext: boolean
    topHeight: number
    isDragging: boolean
}

export class SingleScriptExecution extends React.Component<any, ScriptExecutionState> {

    static contextType = ConfigContext
    context!: React.ContextType<typeof ConfigContext>;

    containerRef = React.createRef<HTMLDivElement>();

    constructor(props) {
        super(props)
        console.log("loading TransportOrder ScriptExecution")

        this.state = {
            isRunning: false,
            validJson: true,
            scriptExeToId: "",
            xRequestId: "",
            clientContext: false,
            topHeight: 0,
            isDragging: false,
        }
    }

    componentDidMount() {
        this.setState({ scriptExeToId: this.context.scriptExeToId })
        requestAnimationFrame(() => {
            if (this.containerRef.current) {
                this.setState({ topHeight: Math.round(this.containerRef.current.clientHeight * 0.67) })
            }
        })
    }

    componentWillUnmount() {
        document.removeEventListener('mousemove', this.onDragMove);
        document.removeEventListener('mouseup', this.onDragEnd);
    }

    onDragStart = (e: React.MouseEvent) => {
        e.preventDefault();
        this.setState({ isDragging: true });
        document.addEventListener('mousemove', this.onDragMove);
        document.addEventListener('mouseup', this.onDragEnd);
    }

    onDragMove = (e: MouseEvent) => {
        if (!this.containerRef.current) return;
        const rect = this.containerRef.current.getBoundingClientRect();
        const newTop = e.clientY - rect.top;
        this.setState({ topHeight: Math.max(150, Math.min(rect.height - 100, newTop)) });
    }

    onDragEnd = () => {
        this.setState({ isDragging: false });
        document.removeEventListener('mousemove', this.onDragMove);
        document.removeEventListener('mouseup', this.onDragEnd);
    }

    async runScript() {

        this.setState({ isRunning: true })
        let auth = backend.withTokenAuthHeader(authentication.token)

        let scriptRequest = {} as ScriptingRequest
        scriptRequest.transportOrderId = this.context.scriptExeToId
        if (this.context.scriptExeToInput != null && this.context.scriptExeToInput != "") {
            scriptRequest.transportOrder = JSON.parse(this.context.scriptExeToInput)
        }
        scriptRequest.groovyScript = this.context.scriptExeScript

        if (this.state.clientContext) {
            await this.runClientContextScript(scriptRequest, auth)
        } else {
            await this.runBackendContextScript(scriptRequest, auth)
        }
        this.setState({ isRunning: false })
    }

    async runBackendContextScript(scriptRequest, auth) {
        let response = await fetch('/api/internal/transportOrders/script', {
            method: 'POST',
            headers: {
                "Authorization": auth.headers.Authorization,
                "Content-Type": "application/json; charset=utf-8"
            },
            body: JSON.stringify(scriptRequest)
        })

        let to = await response.json()
        this.setState({ xRequestId: response.headers.get("x-request-id") })
        let toOutputAsJson = JSON.stringify(to, null, 4)
        this.context.setScriptExeToOutput(toOutputAsJson)
    }

    async runClientContextScript(scriptRequest, auth) {
        let response = await fetch('/script-runner/executeTransportOrderScript', {
            method: 'POST',
            headers: {
                "Authorization": auth.headers.Authorization,
                "Content-Type": "application/json; charset=utf-8"
            },
            body: JSON.stringify(scriptRequest)
        })

        let to = await response.json()
        this.setState({ xRequestId: response.headers.get("x-request-id") })
        let toOutputAsJson = JSON.stringify(to, null, 4)
        this.context.setScriptExeToOutput(toOutputAsJson)
    }

    async handleLoadTransportOrder() {
        let auth = backend.withTokenAuthHeader(authentication.token)
        let transportOrder = await backend.transportOrderApi.fetchTransportOrderById(this.context.scriptExeToId, auth)
        let toAsJson = JSON.stringify(transportOrder, null, 4)
        this.handleTransportOrderChange(toAsJson)
    }

    handleTransportOrderChange(value) {
        let valid = null
        try {
            JSON.parse(value)
            valid = true
        } catch (e) {
            valid = false
        }

        this.context.setScriptExeToInput(value)
        this.context.setScriptExeValid(valid)
        this.setState({ validJson: valid })
    }

    setScriptExeToId(value) {
        this.setState({ scriptExeToId: value })
        this.context.setScriptExeToId(value)
    }

    render() {
        const editorHeight = `${Math.max(this.state.topHeight - 30, 100)}px`;

        return <Form>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                <Input
                    style={{ width: 220 }}
                    placeholder='TransportOrderId'
                    value={this.state.scriptExeToId}
                    onChange={(evt) => this.setScriptExeToId(evt.target.value?.trim())}
                />
                <Button onClick={() => this.handleLoadTransportOrder()}>load TransportOrder</Button>
                <span>
                    Backend Context -
                    <Checkbox
                        style={{ margin: '0 6px' }}
                        type='checkbox'
                        checked={this.state.clientContext}
                        toggle
                        onChange={() => this.setState({ clientContext: !this.state.clientContext })}
                    />
                    - Client Context
                </span>
                <Button onClick={(evt) => this.runScript()} loading={this.state.isRunning}>Execute</Button>
            </div>

            <div
                ref={this.containerRef}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'calc(100vh - 150px)',
                    userSelect: this.state.isDragging ? 'none' : 'auto',
                }}
            >
                <div style={{ height: this.state.topHeight, display: 'flex', gap: 8, flexShrink: 0, overflow: 'hidden' }}>
                    <div style={{ flex: '0 0 30%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Label>Groovy Script</Label>
                        <AceEditor
                            theme="monokai"
                            mode='groovy'
                            value={this.context.scriptExeScript}
                            placeholder='Groovy Script'
                            width="100%"
                            height={editorHeight}
                            onChange={(value) => this.context.setScriptExeScript(value)}
                            readOnly={false}
                        />
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Label>TransportOrder</Label>
                        <AceEditor
                            theme="monokai"
                            mode="json"
                            value={this.context.scriptExeToInput}
                            placeholder='TransportOrder to be modified by script'
                            width='100%'
                            height={editorHeight}
                            onChange={(e) => this.handleTransportOrderChange(e)}
                            readOnly={false}
                        />
                        {!this.state.validJson ? <Label size={'huge'} color='red' pointing>INVALID JSON</Label> : null}
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Label>TransportOrder nach Script</Label>
                        <AceEditor
                            theme="monokai"
                            mode="json"
                            value={this.context.scriptExeToOutput}
                            placeholder='TransportOrder nach Script'
                            width='100%'
                            height={editorHeight}
                            readOnly={true}
                        />
                    </div>
                </div>

                <div
                    onMouseDown={this.onDragStart}
                    style={{
                        height: 8,
                        cursor: 'row-resize',
                        background: this.state.isDragging ? '#bbb' : '#e0e0e0',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <div style={{ width: 40, height: 2, background: '#999', borderRadius: 1 }} />
                </div>

                <div style={{ flex: 1, overflow: 'auto', paddingTop: 4 }}>
                    <h4 style={{ margin: '0 0 4px 0' }}>Logs:</h4>
                    <LogView
                        query={this.state.xRequestId}
                        origin={"singleScript"}
                        initialTimespan={null}
                        responseLogs={null}
                    />
                </div>
            </div>
        </Form>
    }
}